'use client';

import { useForumType } from '@/components/hooks/useForumType';
import React, {useRef, useState, useCallback, createContext, useSyncExternalStore} from 'react';
import classNames from 'classnames'
import { useLocation } from '@/lib/routeUtil';
import { AnalyticsContext } from '@/lib/analyticsEvents'
import { useCurrentUser } from '@/components/common/withUser';
import { DialogManager } from '@/components/common/withDialog';
import { CommentBoxManager } from '@/components/hooks/useCommentBox';
import { ItemsReadContextWrapper } from '@/components/hooks/useRecordPostView';
import { pBodyStyle } from '../../themes/stylePiping';
import { googleTagManagerId } from '@/lib/instanceSettings';
import { globalStyles } from '../../themes/globalStyles/globalStyles';
import { DisableNoKibitzContextProvider } from '@/components/common/sharedContexts';
// enable during ACX Everywhere
// import { HIDE_MAP_COOKIE } from '@/lib/cookies/cookies';
import Header, { HeaderHeightProvider } from '@/components/layout/Header';
import { useCookiePreferences, useCookiesWithConsent } from '@/components/hooks/useCookiesWithConsent';
import { UnreadNotificationsContextProvider } from '@/components/hooks/useUnreadNotifications';
import DeferRender from '@/components/common/DeferRender';
import { userHasLlmChat } from '@/lib/betas';
import ErrorBoundary from "@/components/common/ErrorBoundary";
import FlashMessages from "@/components/layout/FlashMessages";
import AnalyticsClient from "@/components/common/AnalyticsClient";
import AnalyticsPageInitializer from "@/components/common/AnalyticsPageInitializer";
import { CommentOnSelectionPageWrapper } from "@/components/comments/CommentOnSelection";
import SidebarsWrapper from "@/components/layout/SidebarsWrapper";
import GlobalHotkeys from "@/components/common/GlobalHotkeys";
import LlmChatWrapper from "@/components/languageModels/LlmChatWrapper";
import LWBackgroundImage from "./LWBackgroundImage";
import IntercomWrapper from "@/components/layout/IntercomWrapper";
import CookieBanner from "@/components/common/CookieBanner/CookieBanner";
import NavigationEventSender from '@/components/hooks/useOnNavigate';
import { defineStyles, useStyles } from '@/components/hooks/useStyles';
import { SuspenseWrapper } from '@/components/common/SuspenseWrapper';
import { isFullscreenRoute, isHomeRoute, isResearchRoute, isRouteWithLeftNavigationColumn, isStandaloneRoute } from '@/lib/routeChecks';
import { EditorCommandsContextProvider } from '@/components/editor/EditorCommandsContext';
import { SHOW_LLM_CHAT_COOKIE } from '@/lib/cookies/cookies';
import { SubtitlePortalProvider } from './SubtitlePortalContext';

import dynamic from 'next/dynamic';
import { isBlackBarTitle } from '@/components/seasonal/petrovDay/petrov-day-story/petrovConsts';
import { useIsPetrovDayRitualActive } from '@/components/seasonal/petrovDay/petrov-day-story/useIsPetrovDayRitualActive';
import { usePrerenderablePathname } from '../next/usePrerenderablePathname';
import { PopperPortalProvider } from '../common/LWPopper';
import { HideNavigationSidebarContextProvider } from './HideNavigationSidebarContextProvider';
import { getHomeDesignActiveSnapshot, subscribeToHomeDesignActive } from '../common/HomeDesignChatContext';
import { usePathname } from 'next/navigation';

const LanguageModelLauncherButton = dynamic(() => import("../languageModels/LanguageModelLauncherButton"), { ssr: false });
const SidebarLanguageModelChat = dynamic(() => import("../languageModels/SidebarLanguageModelChat"), { ssr: false });

const styles = defineStyles("Layout", (theme: ThemeType) => ({
  navSidebar: {
    gridArea: 'navSidebar'
  },
  languageModelLauncher: {
    position: 'absolute',
    top: '-57px',
    right: '-334px',
    [theme.breakpoints.down('lg')]: {
      display: 'none',
    }
  },
  topLevelContainer: {
    display: 'flex',
    flexDirection: 'row',
    minHeight: '100vh',
    width: '100%',
  },
  pageContent: {
    flexGrow: 1,
    flexShrink: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  llmChatColumn: {
    flexShrink: 0,
    flexGrow: 0,
  },
  '@global': {
    ...globalStyles(theme),
    p: pBodyStyle(theme),
    '.mapboxgl-popup': {
      willChange: 'auto !important',
      zIndex: theme.zIndexes.styledMapPopup
    },
    // Font fallback to ensure that all greek letters just directly render as Arial
    '@font-face': [{
        fontFamily: "GreekFallback",
        src: "local('Arial')",
        unicodeRange: 'U+0370-03FF, U+1F00-1FFF' // Unicode range for greek characters
      },
      {
        fontFamily: "ETBookRoman",
        src: "url('https://res.cloudinary.com/lesswrong-2-0/raw/upload/v1723063815/et-book-roman-line-figures_tvofzs.woff') format('woff')",  
      },
    ],
    // Hide the CKEditor table alignment menu
    '.ck-table-properties-form__alignment-row': {
      display: "none !important"
    },
    // When the sandboxed home page design is active, hide chrome that the
    // design replaces (header, intercom, LLM chat launcher). The class is
    // toggled in PageBackgroundWrapper which lives above the Activity
    // boundary, so it correctly reflects the current route even when
    // cacheComponents keeps old page trees in the DOM.
    '.home-design-active .Header-root': {
      display: 'none !important',
    },
    '.home-design-active .Header-headerHeight': {
      '--header-height': '0px',
    },
    '.home-design-active .RouteRootClient-centralColumn': {
      paddingTop: '0 !important',
    },
    '.home-design-active .home-design-hide-llm-chat': {
      display: 'none !important',
    },
    'body:has(.home-design-active)': {
      overflow: 'hidden !important',
      height: '100dvh !important',
    },
    'body:has(.home-design-active) #intercom-outer-frame, body:has(.home-design-active) #intercom-container, body:has(.home-design-active) .intercom-lightweight-app': {
      display: 'none !important',
    },
    '.research-active .Header-root': {
      display: 'none !important',
    },
    // Zero out the header-height custom property for the whole research
    // subtree. `--header-height` is declared 64px on the HeaderHeightProvider
    // span that *wraps* the page, so `.research-active` (on
    // PageBackgroundWrapper) is a descendant of it — a `.research-active
    // .Header-headerHeight` selector can never match. Declaring the property
    // on `.research-active` itself overrides the inherited value for
    // everything inside, so the full-viewport editor's
    // `min-height: calc(100vh - var(--header-height))` fills the pane instead
    // of leaving a 64px gap at the bottom.
    '.research-active': {
      '--header-height': '0px',
    },
    '.research-active .RouteRootClient-centralColumn': {
      paddingTop: '0 !important',
    },
    'body:has(.research-active)': {
      overflow: 'hidden !important',
      height: '100dvh !important',
    },
  },
  searchResultsArea: {
    position: "absolute",
    zIndex: theme.zIndexes.searchResults,
    top: 0,
    width: "100%",
  },
  // enable during ACX Everywhere
  hideHomepageMapOnMobile: {
    [theme.breakpoints.down('sm')]: {
      display: "none"
    }
  },
}));

const isPathnameWithHiddenFloatingButtons = (pathname: string) =>
  pathname.startsWith('/inbox') || pathname.startsWith('/research') || pathname.startsWith('/admin/supermod');

const Layout = ({children}: {
  children?: React.ReactNode,
}) => {
  const { isLW, forumType } = useForumType();
  const classes = useStyles(styles);
  const currentUser = useCurrentUser();
  const currentUserId = currentUser?._id;
  const searchResultsAreaRef = useRef<HTMLDivElement|null>(null);
  const prerenderablePathname = usePrerenderablePathname();
  const pathname = usePathname();

  // enable during ACX Everywhere
  // const [cookies] = useCookiesWithConsent()
  // replace with following line to enable during ACX Everywhere.
  // also uncomment out the dynamic import and render of the HomepageCommunityMap.
  // (they're commented out to reduce the split bundle size.)
  const renderCommunityMap = false

  // isLW && isHomeRoute(prerenderablePathname) && (!currentUser?.hideFrontpageMap) && !cookies[HIDE_MAP_COOKIE]
  
  const hideIntercom = isPathnameWithHiddenFloatingButtons(prerenderablePathname);

  const petrovDayRitualActive = useIsPetrovDayRitualActive();
  let headerBackgroundColor: ColorString|undefined = undefined;
  if (isBlackBarTitle || (isLW && petrovDayRitualActive)) {
    headerBackgroundColor = 'rgba(0, 0, 0, 0.7)';
  }

  // Check whether the current route is one which should have standalone
  // navigation on the side. If there is no current route (ie, a 404 page),
  // then it should.
  const standaloneNavigation = isRouteWithLeftNavigationColumn(prerenderablePathname);
    
  return (
    <AnalyticsContext path={pathname}>
      <SubtitlePortalProvider>
      <PopperPortalProvider>
      <UnreadNotificationsContextProvider>
      <ItemsReadContextWrapper>
      <SidebarsWrapper>
      <HideNavigationSidebarContextProvider>
      <EditorCommandsContextProvider>
      <LlmChatWrapper>
      <DisableNoKibitzContextProvider>
      <CommentOnSelectionPageWrapper>
        <HeaderHeightProvider>
        <PageBackgroundWrapper>
          <DialogManager>
          <LlmSidebarWrapper>
            <CommentBoxManager>
              <AnalyticsClient/>
              <AnalyticsPageInitializer/>
              <GlobalHotkeys/>
              {/* Only show intercom after they have accepted cookies */}
              <DeferRender ssr={false}>
                <MaybeCookieBanner hideIntercomButton={hideIntercom} />
              </DeferRender>

              <noscript className="noscript-warning"> This website requires javascript to properly function. Consider activating javascript to get access to all site functionality. </noscript>
              {/* Google Tag Manager i-frame fallback */}
              <noscript><iframe src={`https://www.googletagmanager.com/ns.html?id=${googleTagManagerId}`} height="0" width="0" style={{display:"none", visibility:"hidden"}}/></noscript>

              {!isStandaloneRoute(prerenderablePathname) && <SuspenseWrapper name="Header">
                <Header
                  searchResultsArea={searchResultsAreaRef}
                  standaloneNavigationPresent={standaloneNavigation}
                  backgroundColor={headerBackgroundColor}
                />
              </SuspenseWrapper>}
              {/* enable during ACX Everywhere */}
              {renderCommunityMap && <span className={classes.hideHomepageMapOnMobile}>
                {/* <SuspenseWrapper name="HomepageCommunityMap">
                  <HomepageCommunityMap dontAskUserLocation={true}/>
                </SuspenseWrapper> */}
              </span>}

              <ErrorBoundary>
                <FlashMessages />
              </ErrorBoundary>

              {isLW && <LWBackgroundImage />}
              <div ref={searchResultsAreaRef} className={classes.searchResultsArea} />

              {children}
            </CommentBoxManager>
          </LlmSidebarWrapper>
          </DialogManager>
          <NavigationEventSender />
        </PageBackgroundWrapper>
        </HeaderHeightProvider>
      </CommentOnSelectionPageWrapper>
      </DisableNoKibitzContextProvider>
      </LlmChatWrapper>
      </EditorCommandsContextProvider>
      </HideNavigationSidebarContextProvider>
      </SidebarsWrapper>
      </ItemsReadContextWrapper>
      </UnreadNotificationsContextProvider>
      </PopperPortalProvider>
      </SubtitlePortalProvider>
    </AnalyticsContext>
  )
}

function MaybeCookieBanner({ hideIntercomButton }: { hideIntercomButton: boolean }) {
  const { explicitConsentGiven: cookieConsentGiven, explicitConsentRequired: cookieConsentRequired } = useCookiePreferences();
  const showCookieBanner = cookieConsentRequired === true && !cookieConsentGiven;

  if (showCookieBanner) {
    return (
      <CookieBanner />
    );
  }

  return hideIntercomButton ? null : <IntercomWrapper />
}

export const IsLlmChatSidebarOpenContext = createContext(false);

/**
 * Wrapper that splits the layout into a main column and a right-hand sidebar
 * for an LLM chat wrapper, if open. Otherwise may provide a floating button,
 * if the feature is enabled for the current user and route. Provides a context
 * IsLlmChatSidebarOpenContext, which is used by <Header> to adjust its width.
 */
const LlmSidebarWrapper = ({children}: {
  children: React.ReactNode
}) => {
  const { forumType } = useForumType();
  const classes = useStyles(styles);
  const currentUser = useCurrentUser();
  const prerenderablePathname = usePrerenderablePathname();
  const hideLlmChatButton = isPathnameWithHiddenFloatingButtons(prerenderablePathname);
  const [cookies, setCookie] = useCookiesWithConsent([SHOW_LLM_CHAT_COOKIE]);

  const [showLlmChatSidebar, setShowLlmChatSidebar] = useState(false);
  const closeLlmChatSidebar = useCallback(() => {
    setShowLlmChatSidebar(false);
    setCookie(SHOW_LLM_CHAT_COOKIE, "false", { path: "/" });
  }, [setCookie]);

  const renderLanguageModelChatLauncher = !!currentUser && userHasLlmChat(currentUser, forumType) && !hideLlmChatButton;

  return <div className={classes.topLevelContainer}>
    <div className={classes.pageContent}>
      <IsLlmChatSidebarOpenContext.Provider value={showLlmChatSidebar}>
        {children}
      </IsLlmChatSidebarOpenContext.Provider>
    </div>
    {renderLanguageModelChatLauncher && (
      <div className={classNames(classes.llmChatColumn, 'home-design-hide-llm-chat')}>
        <DeferRender ssr={false}>
          {showLlmChatSidebar ? (
            <SuspenseWrapper name="SidebarLanguageModelChat">
              <SidebarLanguageModelChat onClose={closeLlmChatSidebar} />
            </SuspenseWrapper>
          ) : (
            <LanguageModelLauncherButton onClick={() => setShowLlmChatSidebar(true)} />
          )}
        </DeferRender>
      </div>
    )}
  </div>
}

const pageBackgroundWrapperStyles = defineStyles("PageBackgroundWrapper", (theme: ThemeType) => ({
  wrapper: {
    position: 'relative',
  },
  fullscreen: {
    // The min height of 600px here is so that the page doesn't shrink down completely when the keyboard is open on mobile.
    // I chose 600 as being a bit smaller than the smallest phone screen size, although it's hard to find a good reference
    // for this. Here is one site with a good list from 2018: https://mediag.com/blog/popular-screen-resolutions-designing-for-all/
    height: "max(100vh, 600px)",
    display: "flex",
    flexDirection: "column",
  },
}))

function PageBackgroundWrapper({children}: {
  children: React.ReactNode
}) {
  const { isAF, isLW, forumType } = useForumType();
  const classes = useStyles(pageBackgroundWrapperStyles);
  const pathname = usePrerenderablePathname();
  const { query } = useLocation();
  const isHomeDesignActive = useSyncExternalStore(
    subscribeToHomeDesignActive,
    getHomeDesignActiveSnapshot,
    () => false
  );
  const isSandboxedHomePage = isLW && isHomeRoute(pathname, forumType) && (!!query.theme || isHomeDesignActive);

  return <div id="wrapper" className={classNames(
    "wrapper", classes.wrapper, {
      'alignment-forum': isAF,
      [classes.fullscreen]: isFullscreenRoute(pathname),
      'home-design-active': isSandboxedHomePage,
      'research-active': isResearchRoute(pathname),
    },
  )}>
    {children}
  </div>
}

export default Layout;
